// src/api/ProductApi.js
import axios from 'axios';
import { ERROR_CODES } from '../constants/errors'; // Assuming you have this

const baseUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000';
const productsApiUrl = `${baseUrl}/products/api`;

/**
 * Handles API request errors consistently.
 * @param {Error} error - The error object from Axios or other sources.
 * @param {string} functionName - Name of the calling function for logging.
 * @returns {object} Standardized error object { error: string, message: string }.
 */
const handleApiError = (error, functionName) => {
    console.error(`[${functionName}] API Error:`, error);
    if (error.response) {
        // Server responded with a status code outside the 2xx range
        console.error(`[${functionName}] Response Data:`, error.response.data);
        console.error(`[${functionName}] Response Status:`, error.response.status);
        return {
            status: false,
            error: error.response.data?.error || `HTTP_ERROR_${error.response.status}`,
            message: error.response.data?.message || 'An API error occurred.',
            statusCode: error.response.status
        };
    } else if (error.request) {
        // Request was made but no response received
        console.error(`[${functionName}] No response received:`, error.request);
        return {
            status: false,
            error: ERROR_CODES.NETWORK_ERROR, // Use your defined error code
            message: 'No response received from server. Check network connection.',
            statusCode: null
        };
    } else {
        // Something happened in setting up the request
        console.error(`[${functionName}] Request setup error:`, error.message);
        return {
            status: false,
            error: ERROR_CODES.REQUEST_ERROR, // Use your defined error code
            message: error.message || 'Error setting up the request.',
            statusCode: null
        };
    }
};

/**
 * Fetches the list of all products. Requires Admin role via token.
 * @param {string} token - Firebase Auth ID token.
 * @returns {Promise<object>} - { status: true, data: products[] } on success,
 *                             { status: false, error: string, message: string } on failure.
 */
export const fetchProducts = async (token) => {
    const url = `${productsApiUrl}/list`;
    console.debug("[fetchProducts] Attempting to fetch products.");
    try {
        const response = await axios.get(url, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            validateStatus: (status) => status >= 200 && status < 500, // Handle 4xx/5xx in response
        });

        if (response.status === 200) {
            console.debug("[fetchProducts] Successfully fetched products:", response.data);
            return { status: true, data: response.data };
        } else {
            console.warn(`[fetchProducts] Failed. Status: ${response.status}`, response.data);
            return {
                status: false,
                error: response.data?.error || `HTTP_ERROR_${response.status}`,
                message: response.data?.message || `Failed to fetch products (Status: ${response.status})`,
                statusCode: response.status
            };
        }
    } catch (error) {
        return handleApiError(error, 'fetchProducts');
    }
};

/**
 * Creates a new product. Requires Admin role via token.
 * @param {string} token - Firebase Auth ID token.
 * @param {string} productName - The name of the new product.
 * @param {string} description - The description of the new product.
 * @returns {Promise<object>} - { status: true, data: newProduct } on success,
 *                             { status: false, error: string, message: string } on failure.
 */
export const createProduct = async (token, productName, description, goToMarket) => {
    const url = `${productsApiUrl}/create`;
    console.debug(`[createProduct] Attempting to create product: ${productName}`);
    try {
        const response = await axios.post(url,
            { productName, description, goToMarket },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                validateStatus: (status) => status >= 200 && status < 500,
            }
        );

        if (response.status === 201) { // Expect 201 Created
            console.debug("[createProduct] Successfully created product:", response.data);
            return { status: true, data: response.data }; // Return the newly created product object
        } else {
            console.warn(`[createProduct] Failed. Status: ${response.status}`, response.data);
            return {
                status: false,
                error: response.data?.error || `HTTP_ERROR_${response.status}`,
                message: response.data?.message || `Failed to create product (Status: ${response.status})`,
                statusCode: response.status
             };
        }
    } catch (error) {
        return handleApiError(error, 'createProduct');
    }
};

/**
 * Updates an existing product. Requires Admin role via token.
 * @param {string} token - Firebase Auth ID token.
 * @param {number} productId - The ID of the product to update.
 * @param {string} productName - The updated product name.
 * @param {string} description - The updated product description.
 * @returns {Promise<object>} - { status: true, message: string } on success,
 *                             { status: false, error: string, message: string } on failure.
 */
export const updateProduct = async (token, productId, productName, description, goToMarket) => {
    const url = `${productsApiUrl}/update/${productId}`;
    console.debug(`[updateProduct] Attempting to update product ID: ${productId}`);
    try {
        const response = await axios.put(url,
            { productName, description, goToMarket }, // Send both fields
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                validateStatus: (status) => status >= 200 && status < 500,
            }
        );

        if (response.status === 200) {
            console.debug(`[updateProduct] Successfully updated product ID: ${productId}`, response.data);
            return { status: true, message: response.data.message };
        } else {
            console.warn(`[updateProduct] Failed. Status: ${response.status}`, response.data);
            return {
                status: false,
                error: response.data?.error || `HTTP_ERROR_${response.status}`,
                message: response.data?.message || `Failed to update product (Status: ${response.status})`,
                statusCode: response.status
            };
        }
    } catch (error) {
        return handleApiError(error, 'updateProduct');
    }
};

/**
 * Deletes a product. Requires Admin role via token.
 * @param {string} token - Firebase Auth ID token.
 * @param {number} productId - The ID of the product to delete.
 * @returns {Promise<object>} - { status: true, message: string } on success,
 *                             { status: false, error: string, message: string } on failure.
 */
export const deleteProduct = async (token, productId) => {
    const url = `${productsApiUrl}/delete/${productId}`;
    console.debug(`[deleteProduct] Attempting to delete product ID: ${productId}`);
    try {
        const response = await axios.delete(url, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            validateStatus: (status) => status >= 200 && status < 500,
        });

        if (response.status === 200) {
            console.debug(`[deleteProduct] Successfully deleted product ID: ${productId}`, response.data);
            return { status: true, message: response.data.message };
        } else {
            console.warn(`[deleteProduct] Failed. Status: ${response.status}`, response.data);
            return {
                status: false,
                error: response.data?.error || `HTTP_ERROR_${response.status}`,
                message: response.data?.message || `Failed to delete product (Status: ${response.status})`,
                statusCode: response.status
            };
        }
    } catch (error) {
        return handleApiError(error, 'deleteProduct');
    }
};